import type { ToolCallPayload } from "../../../api/threads/liveMessages";
import { appPaths } from "../../../shared/routing/appPaths";
import { parseContentAddress } from "../../../shared/references/contentReferences";

export interface ToolCallLink {
  readonly label: string;
  readonly href: string;
  /** An entry of the Run shown, up the page; otherwise another page (F164: the link says which). */
  readonly inThisRun: boolean;
}

/**
 * Recorded item keys link generically, independent of the tool name (web-app.md WA-4). `threadId` is the call's Run,
 * `shownThreadId` the Run on screen: a fork shows its parent's calls, whose entries are the parent's.
 */
export function toolCallLinks(payload: ToolCallPayload, threadId: string | undefined, shownThreadId?: string): readonly ToolCallLink[] {
  const links = new Map<string, ToolCallLink>();
  function visit(value: unknown): void {
    if (value === null || typeof value !== "object") return;
    if ("reference" in value && typeof value.reference === "string" && "name" in value && typeof value.name === "string" && "kind" in value && typeof value.kind === "string") {
      const address = value.kind === "document" ? parseContentAddress(value.reference) : null;
      const href = address === null ? appPaths.item(value.reference) : appPaths.sourceDocument(address.document, address.segmentPath);
      links.set(href + value.name, { label: value.name, href, inThisRun: false });
    }
    for (const [key, child] of Object.entries(value)) {
      let link: ToolCallLink | null = null;
      if (key === "change_set_id" && typeof child === "string") link = { label: child, href: appPaths.review(child), inThisRun: false };
      if (key === "output_entry" && threadId !== undefined && typeof child === "number" && Number.isSafeInteger(child) && child > 0) link = { label: `#${String(child)}`, href: appPaths.thread(threadId, child), inThisRun: threadId === shownThreadId };
      if (link !== null) links.set(link.href + link.label, link);
      visit(child);
    }
  }
  visit(payload.arguments);
  visit(payload.result_summary);
  return [...links.values()];
}
